// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import ProductGridContainer, {
    ProductGridContainerStyle,
    useProductGridContainerContext,
} from "@guestsupply/App/Components/Product/ProductGridContainer";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@guestsupply/App/Components/Product/ProductPriceAndActions";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@guestsupply/Overrides/Widgets/ProductList/ProductListProductInformation";
import { xScreen } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    // XNG-CHANGES
    productGridContainerStyles?: ProductGridContainerStyle;
    productGridContainerImageStyledWrapper?: InjectableCss;
    productGridContainerInfoStyledWrapper?: InjectableCss;
    infoImageStyledWrapper?: InjectableCss;
    rightSideDetailsContainer?: InjectableCss;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productPriceAndActionsBottomStyles?: ProductPriceAndActionsStyle;
    productListProductInformation?: ProductListProductInformationStyles;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    return (
        <ProductGridContainer
            id={`product_${product.id}`}
            data-test-selector="productContainer"
            extendedStyles={styles.productGridContainerStyles}
        >
            <ProductGridContainer.Image>
                <StyledWrapper {...styles.productGridContainerImageStyledWrapper}>
                    <ProductListProductImage
                        showImage={showImage}
                        showCompare={false}
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                    />
                </StyledWrapper>
            </ProductGridContainer.Image>
            <ProductGridContainer.Info>
                <StyledWrapper {...styles.productGridContainerInfoStyledWrapper}>
                    <StyledWrapper {...styles.infoImageStyledWrapper}>
                        <ProductGridContainer.Image>
                            <ProductListProductImage
                                showImage={showImage}
                                showCompare={false}
                                showImageBadge={showImageBadges}
                                showTextBadge={showTextBadges}
                                {...{ isGridView: false }}
                            />
                        </ProductGridContainer.Image>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSideDetailsContainer}>
                        <ProductListProductInformation
                            showAttributes={showAttributes}
                            showAvailability={false}
                            showBrand={showBrand}
                            showPartNumbers={showPartNumbers}
                            showTitle={showTitle}
                            showImageBadges={showImageBadges}
                            maxImageBadges={maximumNumberOfImageBadges}
                            showTextBadges={showTextBadges}
                            maxTextBadges={maximumNumberOfTextBadges}
                            extendedStyles={styles.productListProductInformation}
                            {...{ isGridView: true }}
                        />
                        <ProductPriceAndActions
                            showAvailability={isTabletScreen && showAvailability}
                            showCompare={false}
                            showAddToList={false}
                            showAddToCartActions={false}
                            showPrice={isTabletScreen && showPrice}
                            showAutoDelivery={isTabletScreen}
                            showDisabled={true}
                            isPlp={true}
                            extendedStyles={styles.productPriceAndActionsStyles}
                            showReplacements={isTabletScreen}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </ProductGridContainer.Info>
            <ProductGridContainer.PriceAndActions>
                <ProductPriceAndActions
                    showAvailability={!isTabletScreen && showAvailability}
                    showCompare={showCompare}
                    showAddToList={showAddToList}
                    showPrice={!isTabletScreen && showPrice}
                    showAutoDelivery={!isTabletScreen}
                    showDisabled={true}
                    extendedStyles={styles.productPriceAndActionsBottomStyles}
                    isPlp={true}
                    showReplacements={!isTabletScreen}
                />
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProduct(ProductListProductGridCard);
